import React, {
  useEffect, useState, useRef,
} from 'react';
import { toast } from 'react-toastify';
import styled from '@emotion/styled';
import Logger from 'js-logger';
import ResizeObserver from 'react-resize-observer';
import { useSelector, useDispatch } from 'react-redux';
import { animated, useTransition, useSpring } from 'react-spring';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import { Form } from 'formik';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

import Text from '../../../../shared/components/Text/Text';
import Button from '../../../../shared/components/Button/Button';
import FormHandler from '../../../../shared/components/FormHandler/FormHandler';
import { fetchExercises, fetchMaxReport } from '../../ducks/reportingActions';
import Spinner from '../../../../shared/components/Spinner/Spinner';

import MaxModalFormSlideOne from './MaxModalFormSlideOne';
import MaxModalFormSlideTwo from './MaxModalFormSlideTwo';

import { TransitionGlobal } from '../../../../shared/GlobalStyles';

import { reportInteractionTracker } from '../../../../shared/utils/amplitudeHelper';

const SlideContainer = styled('div')`
  display: block;
  height: auto;
  height: ${(props) => `${props.height}px`};
  position: relative;
  width: 100%;
  min-height: 184px;
  transition: ${TransitionGlobal};
  z-index: 1000;
`;

const SliderNav = styled('div')`
  display: flex;
  justify-content: space-between;
  align-self: center;
  width: 30px;
  margin: 0 auto;
  margin-top: 30px;
  z-index: 10;
  position: relative;
`;

const NavCircle = styled('div')`
  height: 10px;
  width: 10px;
  display: block;
  border: 1px solid #979797;
  border-radius: 100%;
  cursor: pointer;

  &.active {
    background-color: #d8d8d8;
    border: 1px solid #d8d8d8;
    cursor: default;
  }
`;

const SpinnerContainer = styled('div')`
  display: flex;
  height: 100%;
  flex: 1;
  position: absolute;
  top: 0;
  justify-content: center;
  width: 100%;
  z-index: 0;
  left: 0;
  margin-top: 40px;
`;

const FieldError = styled('div')`
  margin: 10px 0px;
  font-size: 14px;
  color: #ff6600;
`;

const NavigationWrapper = styled('div')`
  display: flex;
  width: 100%;
  margin-bottom: -20px;
  z-index: 1;
  position: absolute;
  top: 0px;
  left: 0px;
  padding: 20px;

  .arrow-div {
    cursor: pointer;

    &.back {
      justify-self: flex-start;
    }
    &.close {
      justify-self: flex-end;
      margin-left: auto;
    }
  }
`;

// const MaxReportSchema = Yup.object().shape({
//   exercise: Yup.string()
//     .required('Required'),

//   exerciseIds: Yup.array()
//     .of(
//       Yup.object().shape({
//         name: Yup.string()
//           .min(4, 'too short')
//           .required('Required'), // these constraints take precedence
//         salary: Yup.string()
//           .min(3, 'cmon')
//           .required('Required'), // these constraints take precedence
//       })
//     )
// });

const defaultSlideStyle = { position: 'absolute', width: '100%' };

const MaxModalForm = ({
  closeModal,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [slideDimensions, setSlideDimensions] = useState({
    height: 0,
    width: 0,
  });
  const slideRef = useRef();
  const theme = useTheme();

  const exercises = useSelector((state) => state.reporting.data.exercises);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const sidebarFilter = useSelector((state) => state.reporting.data.sidebarFilter);
  const maxReportForm = useSelector((state) => state.reporting.data.maxReportForm);
  const maxReportError = useSelector((state) => state.reporting.data.maxReportError);
  const isReportOpen = useSelector((state) => state.reporting.ui.isReportOpen);
  const isLoadingMaxReport = useSelector((state) => state.reporting.ui.isLoadingMaxReport);

  const { accountCode } = currentUser;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchExercises(accountCode));
  }, []);

  const fetchReport = (values) => {
    Logger.debug(sidebarFilter);

    if (sidebarFilter.filterIds.length) {
      dispatch(fetchMaxReport(sidebarFilter, values, accountCode));
    } else {
      toast.error('You must select filters before continuing');

      // Amplitude tracker.
      reportInteractionTracker('Report Error', 'Max', 'Reporting Filter has not been selected.');
    }
  };

  const formAnimation = useSpring({
    opacity: isLoadingMaxReport ? 0 : 1,
    zIndex: 1,
  });

  const spinnerAnimation = useSpring({ opacity: isLoadingMaxReport ? 1 : 0, zIndex: 0 });

  const slideTransition = useTransition(activeSlide, null, {
    from: { opacity: 0, transform: activeSlide === 1 ? 'translate3d(25%, 0, 0)' : 'translate3d(-25%, 0, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    leave: { opacity: 0, transform: activeSlide === 1 ? 'translate3d(-25%, 0, 0)' : 'translate3d(25%, 0, 0)' },
  });

  return (
    <>
      <NavigationWrapper>
        {activeSlide === 1 && (
          <Text
            className='back arrow-div'
            onClick={() => {
              setActiveSlide(0);
            }}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={15}
              icon='left-arrow'
            />
          </Text>
        )}
        <Text
          className='close arrow-div'
          onClick={() => {
            closeModal();
          }}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={13}
            icon='remove'
          />
        </Text>
      </NavigationWrapper>
      <FormHandler
        initialValues={
          Object.keys(maxReportForm).length !== 0 ? (
            maxReportForm
          ) : (
            {
              average: true,
              bodyWeight: false,
              exerciseIds: [''],
              total: false,
              includeGeneratedMaxes: true,
              includeManualMaxes: true,
              rangeType: 0,
              rangeDates: {},
              relativeStrength: false,
            }
          )
        }
        // validationSchema={MaxReportSchema}
        onSubmit={(values, { setSubmitting }) => {
          setTimeout(() => {
            setSubmitting(false);
            Logger.debug('Form Values:', values);
            fetchReport(values);
          });
        }}
      >
        {(formProps) => (
          <Form
            style={{ width: '100%' }}
          >
            <animated.div style={formAnimation}>
              {maxReportError && (
              <FieldError className='text-center'>
                {maxReportError.message === 'should have required property \'filterIds\''
                  ? 'Please select athletes, groups, or calendars from the filter.'
                  : maxReportError.message === 'should be integer'
                    ? 'Please select at least one exercise.'
                    : maxReportError.message}
              </FieldError>
              )}
              {formProps.errors.exercise && formProps.touched.exercise ? (
                <FieldError className='text-center'>{formProps.errors.exercise}</FieldError>
              ) : null}
              <SlideContainer
                height={slideDimensions.height}
              >
                {/* Slider transition that mounts the active slide
                      based on the state object, activeSlide. */}
                {slideTransition.map(({ item, props }) => (
                  item === 0 ? (
                    <animated.div ref={slideRef} style={{ ...defaultSlideStyle, ...props }}>
                      <ResizeObserver
                        onResize={(rect) => {
                          setSlideDimensions(rect);
                        }}
                      />
                      <MaxModalFormSlideOne
                        formProps={formProps}
                        exercises={exercises}
                      />
                    </animated.div>
                  ) : (
                    <animated.div ref={slideRef} style={{ ...defaultSlideStyle, ...props }}>
                      <ResizeObserver
                        onResize={(rect) => {
                          setSlideDimensions(rect);
                        }}
                      />
                      <MaxModalFormSlideTwo
                        formProps={formProps}
                      />
                    </animated.div>
                  )))}
              </SlideContainer>
              <SliderNav>
                <NavCircle
                  className={activeSlide === 0 ? 'active' : ''}
                  onClick={() => {
                    const defaultOption = exercises.filter((exercise) => exercise.id === formProps.values.exercise);
                    console.log('yo', { ...defaultOption[0] });
                    setActiveSlide(0);
                  }}
                />
                <NavCircle
                  className={activeSlide === 1 ? 'active' : ''}
                  onClick={() => setActiveSlide(1)}
                />
              </SliderNav>
            </animated.div>
            <animated.div style={spinnerAnimation}>
              <SpinnerContainer>
                <Spinner />
              </SpinnerContainer>
            </animated.div>
            {activeSlide !== 0 && (
            <Button
              cta='Run Report'
              className='modal-button'
              type='submit'
              customColor={theme.colors.green}
              fullWidth
              noBorder
              large
              square
              primary
              bottom
            />
            )}
          </Form>
        )}
      </FormHandler>
      {activeSlide === 0 && (
        <Button
          onClick={() => setActiveSlide(1)}
          className='modal-button'
          cta='next'
          type='button'
          customColor={theme.colors.green}
          fullWidth
          noBorder
          large
          square
          primary
          bottom
        />
      )}
    </>
  );
};

export default MaxModalForm;
